/**
 * Third-party tracking ids, extracted verbatim from the live site
 * (contact-us + sign-up-success pages, 2026-08-13).
 *
 * Replicated here:
 * - GTM container (carries GA4 + Google Ads tags). Its only form-conversion
 *   trigger is "page URL contains sign-up-success" — the WordPress thank-you
 *   path. Our forms redirect to /contact-success and /quote-success, so the
 *   lead conversion is fired by the success page itself
 *   (src/lib/form-success-tracking.ts), not by the container. GA4's
 *   `new_site_thank_you` key event comes from a GA4 "create event" rule on the
 *   success-page page_view (GA4 ignores page-level gtag events here).
 *   Verified 2026-09-02: no GTM trigger listens for the `form_success`
 *   dataLayer event, and Ads received zero form conversions Aug 28–Sep 2.
 * - Google Ads AW-1011013367: configured by GTM's Google tag (tag on gtm.init);
 *   the site loaded its own gtag.js too until Oct 9 2026, which duplicated
 *   every remarketing hit.
 * - Microsoft Clarity.
 * - LinkedIn Insight tag.
 * - The gclid capture cookie (90 days) that ties Google Ads clicks to
 *   Salesforce leads via the forms' hidden fields.
 *
 * Deliberately NOT replicated:
 * - UA-19601054-1 — Universal Analytics is a dead product.
 * - CleanTalk page script — the bot check runs server-side in the forms
 *   pipeline (src/lib/forms/cleantalk.ts, spam.ts), not as a page script.
 * - Pardot — the form handlers are called server-side by the forms relay
 *   (src/lib/forms/pardot-handler.ts), not by a page script.
 * - ActiveCampaign site tracking (`diffuser.js`, account 252183559) — still
 *   on the live homepage and program pages, deliberately NOT ported: Thomas
 *   confirmed 2026-08-28 that the account is no longer used. Do not
 *   reintroduce it from a WordPress page diff.
 *
 * Also replicated (audited 2026-08-28 against the live homepage, /about-us
 * and /team-building — every WordPress page loads it):
 * - CallRail dynamic number insertion. The WP plugin emits
 *   cdn.callrail.com/companies/980887097/wp-0-5-3/swap.js; the standard
 *   JavaScript-snippet URL below (company id + swap key) serves the
 *   byte-identical script, so the port does not depend on the WP plugin
 *   integration surviving cutover. The account config embedded in that
 *   script: swap target 800.849.8326 → (866) 393-1925 for Google organic
 *   and (866) 519-2279 for Google Business Profile, 180-day cross-subdomain
 *   cookie, mutation observer ON (so numbers rendered after client-side
 *   navigation are swapped too), form capture OFF.
 */
export const TRACKING = {
  gtmContainerId: 'GTM-K7DG4FWJ',
  googleAdsId: 'AW-1011013367',
  /**
   * Google Ads conversion action "new site lead conversion" — the label the
   * GTM container sends on the WordPress thank-you path, read from the
   * container 2026-09-02. Fired by the form success pages.
   */
  googleAdsLeadConversionLabel: 'BSm7CMn1-JsYEPeti-ID',
  clarityProjectId: 'k8zpe0ys13',
  linkedInPartnerId: '4505617',
  /** Cookie written by the gclid capture script and read into form payloads. */
  gclidCookieName: 'gclid',
  gclidCookieDays: 90,
  /**
   * First-touch attribution, written once per visitor by the same script:
   * the first external referrer and the first URL (UTM parameters intact).
   * Forms send both so bcecloud can derive the Lead Source and Pardot's
   * "Referring Site" — document.referrer at submission time is almost always
   * one of our own pages.
   */
  landingReferrerCookieName: 'bce_ref',
  landingUriCookieName: 'bce_landing',
  attributionCookieDays: 90,
  /** Referrers from here are navigation, not attribution. */
  ownHost: 'bestcorporateevents.com',
  /**
   * Google tag gateway path. Cloudflare serves Google's tags from our own
   * domain at this path (zone setting "Google tag gateway", enabled for
   * GTM-K7DG4FWJ), which is what makes the tags ad-blocker-resilient and
   * first-party. Loading the tag from here instead of googletagmanager.com
   * also stops the container being fetched twice — verified 2026-08-28 that
   * `/wugx/gtm.js?id=GTM-K7DG4FWJ` serves the same container.
   *
   * ⚠ If the gateway is ever turned off in Cloudflare, this path 404s and
   * every Google tag stops loading. Check here first if analytics go quiet.
   */
  googleTagGatewayOrigin: 'https://bestcorporateevents.com/wugx',
  /**
   * Olark live chat. The live WordPress site loads it with this site id on
   * the homepage and program pages only (a per-page snippet); the new site
   * loads it everywhere, because chat should not depend on which page a
   * visitor happens to land on.
   */
  olarkSiteId: '8632-159-10-3197',
  /**
   * Chatbox accent, overriding Olark's default green so the widget reads as
   * ours (hq/brand/CLAUDE.md royal). Account-level theme settings still
   * apply to anything this does not set.
   */
  olarkPrimaryColor: '#1851d1',
  /** CallRail company id + swap key, from the live site's swap.js. */
  callRailCompanyId: '980887097',
  callRailSwapKey: '0afd57b516eb31605cf2',
} as const;

/** CallRail's standard JavaScript-snippet URL (dynamic number insertion). */
export const CALLRAIL_SWAP_SCRIPT_URL = `https://cdn.callrail.com/companies/${TRACKING.callRailCompanyId}/${TRACKING.callRailSwapKey}/12/swap.js`;
